import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Camera,
  ChevronDown,
  Facebook,
  Globe,
  Instagram,
  Maximize2,
  MessageCircle,
  PackageCheck,
  Palette,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Truck,
  X,
} from "lucide-react";

import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { track } from "@/lib/analytics";
import {
  EMAIL,
  FACEBOOK,
  INSTAGRAM,
  TELEFONO_E164,
  TELEFONO_VISIBLE,
  WHATSAPP,
  gmailComposeUrl,
} from "@/lib/contacto";
import { productos, type Producto } from "@/lib/productos";
import responsiveImages from "@/lib/responsive-images.json";

const standGoogleNegro = "/productos/stand-google-negro.webp";
const heroVideoPoster = String(
  responsiveImages["/video/hero-stand-nfc-poster.webp"].variants.find(
    ([width]) => width === 640,
  )![1],
);
const Trabajos = lazy(() =>
  import("@/components/site/Trabajos").then((modulo) => ({ default: modulo.Trabajos })),
);
const ContactForm = lazy(() =>
  import("@/components/site/ContactForm").then((modulo) => ({ default: modulo.ContactForm })),
);

const clientes = [
  { nombre: "Mundo Aromas Perfumes", logo: "/logos/mundo-aromas.webp" },
  { nombre: "BGO", logo: "/logos/bgo.webp" },
  { nombre: "Barber King", logo: "/logos/barber-king.webp" },
  { nombre: "Moscowa Restobar", logo: "/logos/moscowa.webp" },
  { nombre: "Full Market", logo: "/logos/full-market.webp" },
  { nombre: "Tencha Matcheria", logo: "/logos/tencha-matcheria.webp" },
  { nombre: "Lavaseco Seven Clean", logo: "/logos/lavaseco-seven-clean.webp" },
  { nombre: "Kantu Food", logo: "/logos/kantu-food.webp" },
  { nombre: "Festa Gelateria", logo: "/logos/festa-gelateria.webp" },
  { nombre: "Óptica Luz", logo: "/logos/optica-luz.webp" },
];

export const MENSAJE_NFC =
  "Hola, vi los productos NFC + QR en la página de Pyme Visible y me gustaría recibir más información.";

function whatsappConMensaje(mensaje: string) {
  return `${WHATSAPP.split("?")[0]}?text=${encodeURIComponent(mensaje)}`;
}

const faqs = [
  [
    "¿Qué pasa si el celular no tiene NFC?",
    "Todos los modelos que incluyen QR permiten abrir el enlace con la cámara del teléfono, incluso si el equipo no tiene NFC.",
  ],
  [
    "¿El producto llega configurado?",
    "Sí. Programamos el chip NFC y, cuando el modelo lo incluye, configuramos también el QR con el enlace acordado antes de entregarlo.",
  ],
  [
    "¿Puede dirigir a algo distinto de Google?",
    "Sí. Dependiendo del producto, puede abrir Google, WhatsApp, Instagram, TikTok, un menú digital u otro enlace.",
  ],
  [
    "¿Puedo personalizarlo con mi marca?",
    "Sí. Podemos adaptar el stand o adhesivo con tu logo, colores y el enlace que necesite tu negocio.",
  ],
  [
    "¿Realizan envíos fuera de Santiago?",
    "Sí. Atendemos a negocios de todo Chile. Antes de confirmar el pedido te informamos el medio, costo y plazo de entrega.",
  ],
  [
    "¿El stand garantiza reseñas de cinco estrellas?",
    "No. El producto facilita que clientes reales lleguen al formulario de reseña. La valoración siempre depende de su experiencia y opinión.",
  ],
];

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Productos NFC + QR para negocios en Chile | Pyme Visible" },
      {
        name: "description",
        content:
          "Stands, tarjetas y llaveros NFC + QR para conectar clientes con Google, WhatsApp e Instagram. Configurados para tu negocio.",
      },
      {
        property: "og:title",
        content: "Productos NFC + QR para negocios en Chile | Pyme Visible",
      },
      {
        property: "og:description",
        content:
          "Stands, tarjetas y llaveros NFC + QR para conectar clientes con Google, WhatsApp e Instagram. Configurados para tu negocio.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Pyme Visible" },
      { property: "og:locale", content: "es_CL" },
      { property: "og:url", content: "https://pymevisible.cl/" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Productos NFC + QR para negocios en Chile | Pyme Visible",
      },
      {
        name: "twitter:description",
        content:
          "Stands, tarjetas y llaveros NFC + QR para conectar clientes con Google, WhatsApp e Instagram. Configurados para tu negocio.",
      },
      {
        property: "og:image",
        content: `https://pymevisible.cl${standGoogleNegro}`,
      },
      {
        property: "og:image:alt",
        content: "Stand NFC + QR para reseñas de Google de Pyme Visible",
      },
      { name: "twitter:image", content: `https://pymevisible.cl${standGoogleNegro}` },
    ],
    links: [
      { rel: "canonical", href: "https://pymevisible.cl/" },
      {
        rel: "preload",
        as: "image",
        href: heroVideoPoster,
        type: "image/webp",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Pyme Visible",
          "@id": "https://pymevisible.cl/#organization",
          url: "https://pymevisible.cl/",
          logo: "https://pymevisible.cl/img/logotipo-pyme-visible-horizontal.png",
          image: "https://pymevisible.cl/productos/stand-google-negro.webp",
          description:
            "Productos NFC + QR configurados para reseñas de Google, WhatsApp y redes sociales.",
          areaServed: { "@type": "Country", name: "Chile" },
          telephone: TELEFONO_E164,
          email: EMAIL,
          sameAs: [INSTAGRAM, FACEBOOK],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: TELEFONO_E164,
            contactType: "sales",
            areaServed: "CL",
            availableLanguage: "Spanish",
          },
          address: { "@type": "PostalAddress", addressCountry: "CL" },
          priceRange: "$",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://pymevisible.cl/#website",
          url: "https://pymevisible.cl/",
          name: "Pyme Visible",
          inLanguage: "es-CL",
          publisher: { "@id": "https://pymevisible.cl/#organization" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(([question, answer]) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: { "@type": "Answer", text: answer },
          })),
        }),
      },
    ],
  }),
});

function Index() {
  return (
    <div className="min-h-screen bg-canvas">
      <Header />
      <main id="contenido">
        <Hero />
        <ConfianzaRapida />
        <Productos />
        <Marcas />
        <Suspense fallback={<SectionFallback label="Cargando trabajos reales" />}>
          <Trabajos />
        </Suspense>
        <Proceso />
        <ServiciosComplementarios />
        <Nosotros />
        <Faq />
        <Contacto />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function HeroMedia() {
  const [videoAmpliado, setVideoAmpliado] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string>();
  const abridorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Pick once, after hydration: source siblings can be reinserted by React and
    // cause the browser to fetch multiple formats. Never ship a desktop fallback
    // URL to mobile before the viewport is known.
    setVideoSrc(
      window.matchMedia("(max-width: 767px)").matches
        ? "/video/hero-stand-nfc-mobile.mp4"
        : "/video/hero-stand-nfc.mp4",
    );
  }, []);

  useEffect(() => {
    if (!videoAmpliado) return;
    const abridor = abridorRef.current;

    const cerrarConEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVideoAmpliado(false);
    };
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", cerrarConEscape);

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener("keydown", cerrarConEscape);
      abridor?.focus({ preventScroll: true });
    };
  }, [videoAmpliado]);

  return (
    <>
      <div className="pv-hero-media relative mx-auto w-full max-w-[330px] sm:max-w-[620px] lg:ml-auto">
        <div className="pv-hero-orb pv-hero-orb--signal absolute -left-5 top-16 h-24 w-24 rounded-full sm:-left-8 sm:h-32 sm:w-32" />
        <div className="pv-hero-orb pv-hero-orb--primary absolute -right-7 bottom-20 h-28 w-28 rounded-full sm:h-36 sm:w-36" />
        <button
          type="button"
          aria-label="Ampliar demostración del stand NFC"
          ref={abridorRef}
          aria-describedby="hero-video-description"
          aria-haspopup="dialog"
          onClick={() => {
            setVideoAmpliado(true);
            track("video_demo_expand", { ubicacion: "hero" });
          }}
          className="pv-hero-media-card group relative block w-full cursor-zoom-in overflow-hidden rounded-[2rem] p-2 text-left sm:rounded-[3.5rem] sm:p-4"
        >
          <video
            src={videoSrc}
            width={1080}
            height={1080}
            aria-describedby="hero-video-description"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={heroVideoPoster}
            disablePictureInPicture
            className="aspect-square w-full rounded-[1.5rem] object-cover sm:rounded-[2.75rem]"
          />
          <span className="pv-video-expand absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full text-white sm:right-8 sm:top-8 sm:h-11 sm:w-11">
            <Maximize2 className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          </span>
        </button>
        <p id="hero-video-description" className="sr-only">
          Demostración de un teléfono detectando el stand NFC y abriendo el formulario de reseña de
          Google de Pyme Visible.
        </p>
      </div>

      {videoAmpliado &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Demostración ampliada del stand NFC"
            className="pv-video-modal fixed inset-0 z-[80] grid place-items-center p-3 sm:p-8"
            onMouseDown={(event) => {
              if (event.currentTarget === event.target) setVideoAmpliado(false);
            }}
          >
            <div className="relative w-full max-w-[min(82vh,860px)]">
              <video
                src={videoSrc}
                width={1080}
                height={1080}
                aria-label="Un teléfono acerca su NFC al stand y abre el formulario de reseña de Google. Demostración sin audio."
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={heroVideoPoster}
                disablePictureInPicture
                className="pv-video-modal-media aspect-square w-full rounded-[1.75rem] object-cover sm:rounded-[2.5rem]"
              />
              <button
                type="button"
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === "Tab") event.preventDefault();
                }}
                aria-label="Cerrar video ampliado"
                onClick={() => setVideoAmpliado(false)}
                className="pv-video-modal-close absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-white sm:right-5 sm:top-5"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function Hero() {
  const [entregaAbierta, setEntregaAbierta] = useState(false);

  return (
    <section id="inicio" className="pv-hero overflow-hidden">
      <div className="pv-hero-grid mx-auto max-w-[1320px] px-5 pb-6 pt-2 sm:px-8 sm:py-16 lg:min-h-[680px] lg:px-10 lg:py-14">
        <div className="pv-hero-heading relative z-10">
          <p className="pv-eyebrow mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-mono text-[0.62rem] font-medium uppercase tracking-[0.14em] text-ink sm:mb-7 sm:px-4 sm:py-2 sm:text-[0.7rem] sm:tracking-[0.16em]">
            <span className="h-2 w-2 rounded-full bg-signal" /> Stand NFC + QR para negocios en
            Chile
          </p>
          <h1 className="pv-display pv-hero-title" aria-label="Más reseñas. Un solo toque.">
            <span className="block overflow-hidden">
              <span className="pv-hero-line">Más</span>
            </span>
            <span className="block overflow-hidden">
              <span className="pv-hero-line">reseñas.</span>
            </span>
            <span className="block overflow-hidden">
              <span className="pv-hero-line">Un toque.</span>
            </span>
          </h1>
        </div>

        <HeroMedia />

        <div className="pv-hero-copy relative z-10 lg:mt-8">
          <p className="pv-hero-description max-w-lg text-sm leading-relaxed text-slate sm:text-lg">
            <span className="sm:hidden">
              Stands con NFC + QR, tarjetas NFC y llaveros configurados y listos para usar.
            </span>
            <span className="hidden sm:inline">
              Stands NFC, llaveros y tarjetas que llevan a tus clientes directo a Google, WhatsApp o
              tus redes. Personalizados, configurados y listos para usar.
            </span>
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:mt-8 sm:flex-row">
            <a
              href="/productos"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("product_catalog_click", { ubicacion: "hero" })}
              className="pv-button pv-button--primary inline-flex min-h-12 items-center justify-center gap-2 px-6 py-3.5 text-base"
            >
              Ver productos <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href="#proceso"
              className="pv-button pv-button--secondary hidden min-h-12 items-center justify-center px-6 py-3.5 text-base sm:inline-flex"
            >
              Cómo funciona
            </a>
            <a
              href="/stand-nfc-qr"
              className="hidden min-h-12 items-center justify-center px-2 py-3 text-sm font-semibold text-primary hover:underline sm:inline-flex"
            >
              Guía de stands NFC
            </a>
          </div>
        </div>

        <div className="pv-hero-delivery relative z-30 flex flex-col items-center text-center">
          <button
            type="button"
            aria-expanded={entregaAbierta}
            aria-controls="detalle-entrega-hero"
            onClick={() => setEntregaAbierta((abierta) => !abierta)}
            className="pv-delivery-trigger inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-full px-4 py-2 font-semibold text-ink"
          >
            <Truck className="h-4 w-4" aria-hidden="true" />
            <span>Entrega gratis en Santiago</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-300 ${entregaAbierta ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
          {entregaAbierta && (
            <div
              id="detalle-entrega-hero"
              className="pv-delivery-popover absolute bottom-[calc(100%+0.75rem)] left-1/2 w-[min(36rem,calc(100vw-2.5rem))] -translate-x-1/2 rounded-2xl p-4 text-sm font-normal leading-relaxed text-slate"
            >
              Entrega habitual en un día hábil dentro de nuestra zona urbana central. Los pedidos
              confirmados antes de las 13:00 pueden entregarse el mismo día, según disponibilidad y
              ruta. Sectores alejados, como San Bernardo o Padre Hurtado, se coordinan en un punto
              de encuentro.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ConfianzaRapida() {
  const items = [
    {
      icon: PackageCheck,
      title: "Configurado por nosotros",
      text: "NFC y QR revisados antes de entregar.",
    },
    {
      icon: Smartphone,
      title: "Fácil para tus clientes",
      text: "Acercan el teléfono o escanean el código.",
    },
    {
      icon: Truck,
      title: "Santiago y regiones",
      text: "Entrega local y envíos a todo Chile.",
    },
  ];

  return (
    <section className="pv-trust-section px-3 sm:px-8 lg:px-10">
      <div className="pv-trust-rail mx-auto grid max-w-[1240px] grid-cols-3 gap-0">
        {items.map(({ icon: Icon, title, text }, index) => (
          <div
            key={title}
            className={`flex flex-col items-center gap-2 px-1.5 py-4 text-center sm:flex-row sm:items-start sm:gap-4 sm:px-7 sm:py-6 sm:text-left ${
              index > 0 ? "border-l border-ink/10" : ""
            }`}
          >
            <Icon
              className="h-5 w-5 shrink-0 text-primary sm:mt-0.5 sm:h-6 sm:w-6"
              aria-hidden="true"
            />
            <div>
              <p className="text-[0.7rem] font-semibold leading-tight text-ink sm:text-base">
                {title}
              </p>
              <p className="mt-1 hidden text-sm leading-relaxed text-slate sm:block">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductoCard({ producto, index }: { producto: Producto; index: number }) {
  const escalaImagen =
    producto.imageZoom === "keychain"
      ? "scale-[1.45] group-hover:scale-[1.5]"
      : producto.imageZoom === "medium"
        ? "scale-[1.1] group-hover:scale-[1.14]"
        : producto.imageFit === "contain"
          ? "group-hover:scale-[1.02]"
          : "group-hover:scale-[1.035]";

  return (
    <Reveal delay={index * 55}>
      <a
        href={`/productos/${producto.id}`}
        onClick={() => track("product_cta_click", { producto: producto.name, ubicacion: "home" })}
        className="group block h-full rounded-[1.25rem] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary sm:rounded-[2rem]"
      >
        <article
          id={producto.id}
          className="pv-product-card flex h-full scroll-mt-36 flex-col overflow-hidden rounded-[1.25rem] bg-white sm:rounded-[2rem]"
        >
          <div
            className={`pv-product-media relative h-32 overflow-hidden sm:h-64 ${producto.imageSurface ?? "bg-mist"}`}
          >
            <ResponsiveImage
              sizes="(max-width: 639px) 45vw, (max-width: 1023px) 30vw, 280px"
              src={producto.image}
              width={1200}
              height={1600}
              loading="lazy"
              alt={producto.name}
              className={`absolute inset-0 h-full w-full object-center transition-transform duration-700 ${
                producto.imageFit === "contain" ? "object-contain" : "object-cover"
              } ${escalaImagen}`}
            />
            {producto.featured && (
              <span className="pv-featured-badge absolute left-2 top-2 rounded-full px-2 py-1 font-mono text-[0.52rem] font-medium uppercase tracking-[0.08em] text-ink sm:left-5 sm:top-5 sm:px-4 sm:py-2 sm:text-[0.65rem] sm:tracking-[0.12em]">
                Más vendido
              </span>
            )}
          </div>
          <div className="pv-product-body flex flex-1 flex-col p-3 sm:p-5">
            <h3 className="text-base font-extrabold uppercase leading-[0.95] text-ink sm:text-2xl">
              {producto.name}
            </h3>
            <p className="mt-3 hidden line-clamp-2 flex-1 text-sm leading-relaxed text-slate sm:block">
              {producto.description}
            </p>
            <div className="mt-3 flex flex-1 flex-col justify-end gap-2 border-t border-ink/10 pt-3 sm:mt-5 sm:flex-row sm:items-end sm:justify-between sm:gap-3 sm:pt-4">
              <div>
                <p className="hidden font-mono text-[0.65rem] uppercase tracking-[0.14em] text-slate sm:block">
                  Precio
                </p>
                <p className="text-base font-bold text-ink sm:mt-1 sm:text-xl">{producto.price}</p>
              </div>
              <span className="pv-product-cta inline-flex min-h-9 items-center justify-center gap-1 rounded-xl px-2 py-2 text-[0.7rem] font-semibold text-white sm:min-h-10 sm:gap-1.5 sm:px-3.5 sm:py-2.5 sm:text-xs">
                Cotizar <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
              </span>
            </div>
          </div>
        </article>
      </a>
    </Reveal>
  );
}

function Productos() {
  return (
    <section id="productos" className="pv-products-section scroll-mt-24">
      <div className="mx-auto max-w-[1320px] px-5 py-7 sm:px-8 sm:py-16 lg:px-10">
        <Reveal className="max-w-3xl">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">Productos</p>
            <h2 className="mt-2 text-3xl font-extrabold uppercase leading-[0.92] text-ink sm:mt-3 sm:text-5xl">
              Elige cómo quieres conectar.
            </h2>
          </div>
        </Reveal>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-8 sm:gap-4 lg:grid-cols-4">
          {productos.map((producto, index) => (
            <ProductoCard key={producto.id} producto={producto} index={index} />
          ))}
        </div>
        <div className="mt-5 flex justify-center sm:mt-7">
          <a
            href="/productos"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("product_catalog_click", { ubicacion: "home_catalog" })}
            className="pv-button pv-button--primary inline-flex min-h-12 items-center justify-center gap-2 px-7 py-3.5 text-base"
          >
            Abrir catálogo completo <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Marcas() {
  const repetidos = [...clientes, ...clientes];

  return (
    <section
      className="pv-brand-strip border-y border-ink/10 py-6 sm:py-9"
      aria-labelledby="marcas-titulo"
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
          Clientes reales
        </p>
        <h2
          id="marcas-titulo"
          className="mt-2 text-3xl font-extrabold uppercase leading-none text-ink sm:text-5xl"
        >
          Empresas con las que hemos trabajado
        </h2>
      </div>
      <div className="pv-marquee mt-5 sm:mt-7">
        <div className="pv-marquee-track">
          {repetidos.map((cliente, index) => (
            <span
              key={`${cliente.nombre}-${index}`}
              className="pv-logo-tile mx-1.5 grid h-14 w-32 shrink-0 place-items-center rounded-xl px-3 py-2 transition-transform duration-300 hover:scale-105 sm:mx-2 sm:h-20 sm:w-44 sm:rounded-2xl sm:px-5 sm:py-3"
              aria-hidden={index >= clientes.length}
            >
              <ResponsiveImage
                sizes="(max-width: 639px) 100px, 136px"
                src={cliente.logo}
                alt={index < clientes.length ? `Logo de ${cliente.nombre}` : ""}
                width={240}
                height={120}
                loading="lazy"
                className="h-9 w-full object-contain sm:h-14"
              />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

const etapas = [
  {
    number: "01",
    title: "Elige",
    text: "Cuéntanos qué producto necesitas y a qué enlace quieres dirigirlo.",
  },
  {
    number: "02",
    title: "Personalizamos",
    text: "Recibimos tu logo y colores cuando el modelo permite diseño a medida.",
  },
  {
    number: "03",
    title: "Configuramos",
    text: "Programamos NFC y QR, comprobando ambos antes de la entrega.",
  },
  {
    number: "04",
    title: "Recibe",
    text: "Tu producto llega listo para instalar y comenzar a utilizar.",
  },
];

function Proceso() {
  return (
    <section id="proceso" className="pv-process-section scroll-mt-24">
      <div className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 sm:py-16 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
            Cómo funciona
          </p>
          <h2 className="mt-2 max-w-4xl text-3xl font-extrabold uppercase leading-[0.92] text-ink sm:mt-3 sm:text-5xl">
            De tu idea al mesón, sin complicaciones.
          </h2>
        </Reveal>

        <ol className="pv-process-grid mt-5 grid gap-3 overflow-hidden rounded-[1.5rem] sm:mt-8 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
          {etapas.map((etapa, index) => (
            <Reveal as="li" key={etapa.number} delay={index * 70} className="pv-process-card">
              <div className="h-full p-4 sm:p-6">
                <span className="font-mono text-xs text-primary">{etapa.number}</span>
                <h3 className="mt-2 text-2xl font-extrabold uppercase leading-none text-ink sm:mt-6 sm:text-3xl">
                  {etapa.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate sm:mt-3">{etapa.text}</p>
              </div>
            </Reveal>
          ))}
        </ol>

        <Reveal className="pv-inline-cta mt-4 flex flex-col items-start justify-between gap-3 rounded-[1.5rem] p-4 sm:mt-6 sm:flex-row sm:items-center sm:gap-5 sm:p-6">
          <div>
            <p className="font-semibold text-ink">¿No sabes qué modelo elegir?</p>
            <p className="mt-1 max-w-2xl text-sm text-slate sm:mt-2 sm:text-base">
              Cuéntanos dónde lo utilizarás y te ayudamos a elegir el formato adecuado.
            </p>
          </div>
          <a
            href={whatsappConMensaje(MENSAJE_NFC)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("whatsapp_click", { ubicacion: "proceso" })}
            className="pv-button pv-button--primary inline-flex min-h-11 shrink-0 items-center gap-2 px-5 py-3 text-sm"
          >
            <MessageCircle className="h-4 w-4" /> Consultar por WhatsApp
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function ServiciosComplementarios() {
  const servicios = [
    {
      icon: Globe,
      title: "Perfil de Google",
      text: "Creamos u optimizamos la ficha que recibirá las visitas desde tu stand.",
    },
    {
      icon: Camera,
      title: "Fotografía",
      text: "Imágenes profesionales para mostrar mejor tu negocio, productos y espacio.",
    },
    {
      icon: Sparkles,
      title: "Sitio web",
      text: "Una presencia digital clara para explicar tu negocio y facilitar el contacto.",
    },
  ];

  return (
    <section id="servicios" className="pv-services-section scroll-mt-24">
      <div className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 sm:py-16 lg:px-10">
        <Reveal className="max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
            También podemos ayudarte
          </p>
          <h2 className="mt-2 text-3xl font-extrabold uppercase leading-[0.92] text-ink sm:mt-3 sm:text-5xl">
            Haz visible todo lo que hay detrás.
          </h2>
          <p className="mt-4 hidden max-w-2xl text-base leading-relaxed text-slate sm:block">
            El producto NFC es el punto de entrada. Si tu presencia digital todavía necesita apoyo,
            estos servicios pueden completar el recorrido.
          </p>
        </Reveal>

        <div className="mt-5 grid gap-3 sm:mt-8 sm:gap-4 md:grid-cols-3">
          {servicios.map(({ icon: Icon, title, text }, index) => (
            <Reveal key={title} delay={index * 80}>
              <article className="pv-surface-card h-full rounded-[1.25rem] p-4 sm:rounded-[1.5rem] sm:p-6">
                <span className="pv-icon-well grid h-10 w-10 place-items-center rounded-xl text-primary sm:h-12 sm:w-12">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 text-xl font-extrabold uppercase leading-none text-ink sm:mt-5 sm:text-2xl">
                  {title}
                </h3>
                <p className="mt-3 hidden text-sm leading-relaxed text-slate sm:block">{text}</p>
                <a
                  href="#contacto"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-ink underline decoration-primary decoration-2 underline-offset-4 sm:mt-5"
                >
                  Consultar servicio <ArrowRight className="h-4 w-4" />
                </a>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Nosotros() {
  return (
    <section id="nosotros" className="pv-dark-section scroll-mt-24 text-white">
      <div className="mx-auto grid max-w-[1320px] gap-6 px-5 py-10 sm:gap-12 sm:px-8 sm:py-24 lg:grid-cols-[1fr_.8fr] lg:items-end lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-signal">
            Sobre Pyme Visible
          </p>
          <h2 className="pv-section-title mt-4 max-w-4xl text-white">
            Nacimos trabajando cara a cara con negocios locales.
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-base leading-relaxed text-white/75 sm:text-lg">
            Pyme Visible comenzó llevando stands NFC + QR directamente a pymes. Esa experiencia
            mostró algo simple: cuando una herramienta se entiende rápido y llega lista para usar,
            puede integrarse de verdad al día a día del negocio.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-8">
            <span className="inline-flex items-center gap-2 text-sm text-white/80">
              <ShieldCheck className="h-5 w-5 text-signal" /> Trato directo
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-white/80">
              <Palette className="h-5 w-5 text-signal" /> Personalización real
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="pv-faq-section">
      <div className="mx-auto grid max-w-[1320px] gap-6 px-5 py-10 sm:gap-10 sm:px-8 sm:py-24 lg:grid-cols-[.7fr_1.3fr] lg:gap-20 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">FAQ</p>
          <h2 className="pv-section-title mt-4">Preguntas frecuentes.</h2>
        </Reveal>
        <div>
          {faqs.map(([question, answer]) => (
            <Reveal key={question}>
              <details className="pv-accordion-row group border-b border-ink/15 py-4 sm:py-6">
                <summary className="cursor-pointer list-none pr-10 text-lg font-bold text-ink marker:hidden sm:text-xl">
                  {question}
                </summary>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate sm:mt-4 sm:text-base">
                  {answer}
                </p>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contacto() {
  return (
    <section id="contacto" className="pv-contact-section scroll-mt-24">
      <div className="mx-auto grid max-w-[1320px] gap-6 px-5 py-10 pb-24 sm:gap-10 sm:px-8 sm:py-24 sm:pb-32 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">Contacto</p>
          <h2 className="pv-section-title mt-4">Cuéntanos qué quieres conectar.</h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-slate sm:mt-6 sm:text-lg">
            Escríbenos qué producto te interesa y cuál es el enlace de tu negocio. Te orientamos con
            el modelo, personalización y entrega.
          </p>
          <div className="mt-5 grid gap-3 sm:mt-8 sm:gap-4">
            <a
              className="pv-button pv-button--primary inline-flex w-fit items-center gap-2 px-5 py-3 text-sm"
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { ubicacion: "contacto" })}
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp {TELEFONO_VISIBLE}
            </a>
            <a
              className="w-fit font-medium text-ink underline underline-offset-4"
              href={gmailComposeUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("email_click", { ubicacion: "contacto" })}
            >
              {EMAIL}
            </a>
            <div className="flex flex-wrap gap-2 pt-2">
              <a
                href={INSTAGRAM}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("social_click", { red: "instagram", ubicacion: "contacto" })}
                className="pv-social-chip inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold text-ink transition-transform hover:scale-105"
              >
                <Instagram className="h-4 w-4 text-primary" aria-hidden="true" /> Instagram
              </a>
              <a
                href={FACEBOOK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("social_click", { red: "facebook", ubicacion: "contacto" })}
                className="pv-social-chip inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold text-ink transition-transform hover:scale-105"
              >
                <Facebook className="h-4 w-4 text-primary" aria-hidden="true" /> Facebook
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={90}>
          <Suspense
            fallback={
              <div
                className="min-h-72 animate-pulse rounded-[2rem] bg-white/60"
                aria-label="Cargando formulario de contacto"
              />
            }
          >
            <ContactForm />
          </Suspense>
        </Reveal>
      </div>
    </section>
  );
}

function SectionFallback({ label }: { label: string }) {
  return (
    <section
      className="mx-auto min-h-[36rem] max-w-[1320px] px-5 py-8 sm:px-8 sm:py-12"
      aria-label={label}
    >
      <div className="h-full min-h-[30rem] animate-pulse rounded-[2rem] bg-white/45" />
    </section>
  );
}
